/* eslint react/no-array-index-key: 0 */
import { useState, useEffect, useRef } from 'react';
import {
  OverlayView, OverlayViewF, InfoBoxF, MarkerF,
} from '@react-google-maps/api';
import { CarMarkerIcon, HeadingMarkerIcon } from './MarkerIcons';
import {
  getPosition,
  getAngle,
  OVERLAY_Z_IDX,
} from '../helpers';
import { DEFAULT_MARKER_COLOR } from '../markerColors';
import VehicleInfo from './VehicleInfo';
import S from './VehicleInfoStyles';
import { logger } from '../../../../API/services/SharedService';

function VehicleMarker({
  path, fleetId, entityId, entityType, zoom, noAnimation = false, speedMs, showSpeedInVehicleInfo = true, showDetailsButton = true,
  color = DEFAULT_MARKER_COLOR,
}: any): JSX.Element | null {
  const [showInfoBox, setShowInfoBox] = useState(false);
  const infoBoxRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState(getPosition(path));
  const [rotation, setRotation] = useState(getAngle(path));
  const isSinglePoint = path.length === 1;

  const onClickMarker = (): void => {
    setShowInfoBox(true);
  };

  const handleClickOutside = (event: MouseEvent): void => {
    if (infoBoxRef.current && !infoBoxRef.current.contains(event.target as Node)) {
      setShowInfoBox(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, []);

  const pathRef = useRef(path);
  const positionRef = useRef(position);
  const rotationRef = useRef(rotation);
  positionRef.current = position;
  rotationRef.current = rotation;

  // While the cursor is over the marker, pause its movement so the user has
  // a chance to click it instead of chasing a moving target. Capped at
  // MAX_HOVER_FREEZE_MS so a long hover doesn't stall the animation indefinitely.
  const MAX_HOVER_FREEZE_MS = 5000;
  const hoverFreezeUntilRef = useRef<number>(0);

  const onMouseEnterMarker = (): void => {
    hoverFreezeUntilRef.current = Date.now() + MAX_HOVER_FREEZE_MS;
  };

  const onMouseLeaveMarker = (): void => {
    hoverFreezeUntilRef.current = 0;
  };

  useEffect(() => {
    logger.info('VehicleMarker useEffect', { path, zoom });

    // `zoom` is only used to decide whether to animate or snap instantly, and
    // it changes continuously while the user pinches/scrolls the map. Re-running
    // this effect on every zoom tick calls setPosition/setRotation (even when the
    // values don't change), which forces OverlayViewF to tear down and rebuild its
    // DOM node while the user is mid-gesture — that's what makes the marker's click
    // target intermittently miss in Chrome. Only actually animate/snap when the
    // path changed; a zoom-only change just updates which mode future path changes
    // will use.
    const pathChanged = pathRef.current !== path;
    pathRef.current = path;
    if (!pathChanged) {
      return undefined;
    }

    if (zoom <= 10) {
      setPosition(getPosition(path));
      setRotation(getAngle(path));
      return undefined;
    }
    let animationFrameId: number | null = null;

    const startPosition = positionRef.current;
    const endPosition = getPosition(path);
    const startRotation = rotationRef.current;
    const endRotation = getAngle(path);
    const startTime = Date.now();
    const duration = noAnimation ? 5 : 5000;

    let deltaRotation = endRotation - startRotation;
    if (deltaRotation > 180) {
      deltaRotation -= 360;
    } else if (deltaRotation < -180) {
      deltaRotation += 360;
    }

    // OverlayViewF (from @react-google-maps/api) re-creates its underlying
    // google.maps.OverlayView (and re-appends its DOM container) every time the
    // `position` prop object changes. Committing a new position on every RAF tick
    // (up to 60x/sec) therefore tears down and rebuilds the marker's clickable DOM
    // node continuously, so a click can land on a frame where the node is mid
    // teardown/rebuild. Throttle state commits to ~10/sec instead, which stays
    // visually smooth while keeping the DOM node stable long enough to be clickable.
    const STATE_UPDATE_INTERVAL_MS = 100;
    let lastStateUpdate = 0;

    let pausedDuration = 0;
    let lastFrameTime = Date.now();

    const animate = (): void => {
      const currentTime = Date.now();
      if (currentTime < hoverFreezeUntilRef.current) {
        pausedDuration += currentTime - lastFrameTime;
        lastFrameTime = currentTime;
        animationFrameId = requestAnimationFrame(animate);
        return;
      }
      lastFrameTime = currentTime;

      const elapsedTime = currentTime - startTime - pausedDuration;
      const fraction = Math.min(elapsedTime / duration, 1);

      const nextLat = startPosition.lat + (endPosition.lat - startPosition.lat) * fraction;
      const nextLng = startPosition.lng + (endPosition.lng - startPosition.lng) * fraction;
      const nextRotation = startRotation + deltaRotation * fraction;

      if (fraction === 1 || currentTime - lastStateUpdate >= STATE_UPDATE_INTERVAL_MS) {
        lastStateUpdate = currentTime;
        setPosition({ lat: nextLat, lng: nextLng });
        setRotation(nextRotation);
      }

      if (fraction < 1) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    animate();

    return () => {
      logger.info('VehicleMarker useEffect cleanup');
      if (animationFrameId) {
        logger.info('VehicleMarker useEffect: CANCEL ANIMATION FRAME');
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [path, zoom]);

  return (
    <>
      <OverlayViewF
        position={position}
        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
        zIndex={OVERLAY_Z_IDX}
        getPixelPositionOffset={(width: number, height: number) => ({
          x: -(width / 2),
          y: -(height / 2),
        })}
      >
        <S.VehicleMarkerS>
          <button
            type="button"
            className={showInfoBox ? 'active-marker' : ''}
            style={{
              transform: `rotate(${rotation}deg)`,
            }}
            onClick={onClickMarker}
            onMouseEnter={onMouseEnterMarker}
            onMouseLeave={onMouseLeaveMarker}
          >
            {isSinglePoint ? <CarMarkerIcon color={color} /> : <HeadingMarkerIcon color={color} />}
          </button>
        </S.VehicleMarkerS>
      </OverlayViewF>
      <MarkerF position={position} visible={false}>
        {showInfoBox && (
          <InfoBoxF
            options={{
              boxStyle: {
                width: 'fit-content',
                maxWidth: '400px',
              },
              closeBoxURL: '',
              infoBoxClearance: new window.google.maps.Size(24, 24),
              pixelOffset: new window.google.maps.Size(-100, 80),
              alignBottom: true,
            }}
          >
            <VehicleInfo
              ref={infoBoxRef}
              fleetId={fleetId}
              entityId={entityId}
              entityType={entityType}
              speedMs={speedMs}
              showSpeedInVehicleInfo={showSpeedInVehicleInfo}
              showDetailsButton={showDetailsButton}
            />
          </InfoBoxF>
        )}
      </MarkerF>
    </>
  );
}

export default VehicleMarker;
